import { useState, useEffect } from "react";
import { Box, Typography, Stack, Paper, CircularProgress, Chip, Collapse } from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { getLineStationsByLineId, getTripStationNotes } from "../api/trips";
import CarrierLogo from "./CarrierLogo";
import { fmtTime } from "../utils/format";


// Backend lineStations API vraća `departureTime`/`arrivalTime` kao **elapsed**
// vremena od početka linije (npr. linija krene u 23:50, naša polazna stanica
// je u "00:10" elapsed → znači stigne 20 min posle polaska linije). Frontend
// želi da prikaže **clock time** sa kupčeve perspektive (kad bus stiže/polazi
// u realnom vremenu).
//
// Algoritam: za polaznu stanicu (selectedTrip.departureTime) znamo clock time.
// Za svaku sledeću stanicu računamo delta (elapsed - firstElapsed) pa dodajemo
// na clock time polazne. Ako delta postaje negativna, linija je prešla ponoć
// → dodaj 24h.

function parseHHMM(value) {
    if (typeof value !== "string") return null;
    const match = value.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

function formatMinutes(total) {
    if (total === null || total === undefined) return "";
    const wrapped = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
    const h = Math.floor(wrapped / 60);
    const m = wrapped % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export default function TripItinerary({ trip }) {
    const { t } = useTranslation();
    const [stations, setStations] = useState([]);
    const [notesByStation, setNotesByStation] = useState({});
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);

    const lineId = trip?.lineId;
    const tripId = trip?.tripId ?? trip?.id;
    const depStationId = trip?.departureStationId || trip?.fromStationId;
    const arrStationId = trip?.arrivalStationId || trip?.toStationId;

    useEffect(() => {
        if (!lineId) {
            setLoading(false);
            return;
        }
        const fetch = async () => {
            try {
                const data = await getLineStationsByLineId(lineId);
                const list = data?.content || data || [];
                setStations(list);
                // Napomene su dodatak: njihov pad ne sme da sakrije itinerer.
                if (tripId) {
                    try {
                        const notes = await getTripStationNotes(tripId);
                        const map = {};
                        notes.forEach((n) => { map[n.stationId] = n.comment; });
                        setNotesByStation(map);
                    } catch (noteErr) {
                        console.error("Greška pri učitavanju napomena stanica:", noteErr);
                    }
                }
            } catch (err) {
                console.error("Greška pri učitavanju stanica:", err);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [lineId, tripId]);

    if (loading) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} />
            </Paper>
        );
    }

    if (stations.length === 0) return null;

    // Pronadji indekse polazne i dolazne stanice
    let depIdx = stations.findIndex((s) => s.stationId === depStationId || s.station?.id === depStationId);
    let arrIdx = stations.findIndex((s) => s.stationId === arrStationId || s.station?.id === arrStationId);

    if (depIdx === -1) depIdx = 0;
    if (arrIdx === -1) arrIdx = stations.length - 1;

    const routeStations = stations.slice(depIdx, arrIdx + 1);

    if (routeStations.length === 0) return null;

    const operatorId = trip?.operatorId || trip?.carrierId || null;
    const operatorName = trip?.operatorName || trip?.carrierName || "";

    // Clock time naše polazne stanice (npr. "02:00") iz trip podataka.
    const tripDepClockRaw = trip?.departureTime || trip?.departureTimeStr || trip?.calculatedDepartureTime;
    const tripDepClockMinutes = parseHHMM(fmtTime(tripDepClockRaw));

    // Elapsed vreme prve stanice u našem segmentu — referentna tačka za delta.
    const firstStationElapsed = parseHHMM(routeStations[0]?.departureTime || routeStations[0]?.arrivalTime);

    // Da li možemo da prebacimo elapsed → clock? Samo ako oba parse-uju u broj.
    const canConvertToClock = tripDepClockMinutes !== null && firstStationElapsed !== null;

    return (
        <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box
                sx={{ bgcolor: "primary.main", px: 2.5, py: 1.5, cursor: "pointer" }}
                onClick={() => setExpanded((prev) => !prev)}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Icon icon="mdi:map-marker-path" width={18} color="white" />
                        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: 700 }}>
                            {t("booking.itineraryTitle")}
                        </Typography>
                        <Chip
                            label={`${routeStations.length} ${t("booking.stations")}`}
                            size="small"
                            sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                        />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CarrierLogo operatorId={operatorId} size={32} />
                        {operatorName && (
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
                                {operatorName}
                            </Typography>
                        )}
                        <Icon
                            icon={expanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                            width={22}
                            color="white"
                        />
                    </Stack>
                </Stack>
            </Box>

            <Collapse in={expanded}>
            <Box sx={{ p: 2.5 }}>
                {routeStations.map((ls, index) => {
                    const isFirst = index === 0;
                    const isLast = index === routeStations.length - 1;
                    const stationName = ls.stationName || ls.station?.name || "";
                    const stationNote = notesByStation[ls.stationId || ls.station?.id];
                    const depTime = fmtTime(ls.departureTime);
                    const arrTime = fmtTime(ls.arrivalTime);
                    const platform = ls.platformNumber || ls.platform || null;

                    // Clock time (npr. 02:10) umesto elapsed time (00:10).
                    let displayTime;
                    if (canConvertToClock) {
                        // Za prvu stanicu u segmentu: delta je 0 (njeno clock = trip departureTime).
                        // Za ostale: delta = (njena elapsed) - (firstStation elapsed).
                        const stationElapsed = parseHHMM(
                            isFirst ? ls.departureTime : (ls.arrivalTime || ls.departureTime)
                        );
                        if (stationElapsed !== null) {
                            let delta = stationElapsed - firstStationElapsed;
                            // Negativan delta = linija prešla ponoć posle naše polazne
                            if (delta < 0) delta += 24 * 60;
                            displayTime = formatMinutes(tripDepClockMinutes + delta);
                        }
                    }
                    // Fallback ako ne može konverzija (nedostaje trip clock ili elapsed)
                    if (!displayTime) {
                        displayTime = isFirst ? depTime : (arrTime || depTime);
                    }

                    return (
                        <Stack key={ls.id || index} direction="row" spacing={0}>
                            {/* Timeline */}
                            <Box sx={{ width: 24, display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                                <Box
                                    sx={{
                                        width: isFirst || isLast ? 14 : 10,
                                        height: isFirst || isLast ? 14 : 10,
                                        borderRadius: "50%",
                                        bgcolor: isFirst || isLast ? "primary.main" : "white",
                                        border: "2px solid",
                                        borderColor: "primary.main",
                                        flexShrink: 0,
                                        mt: 0.3,
                                    }}
                                />
                                {!isLast && (
                                    <Box sx={{ width: 2, flex: 1, bgcolor: "grey.300", minHeight: 20 }} />
                                )}
                            </Box>

                            {/* Sadrzaj */}
                            <Box sx={{ flex: 1, pb: isLast ? 0 : 1.5, ml: 1.5 }}>
                                <Stack direction="row" spacing={1.5} alignItems="baseline">
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 40,
                                            fontFamily: "monospace",
                                            color: isFirst || isLast ? "primary.main" : "text.primary",
                                        }}
                                    >
                                        {displayTime || "--:--"}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: isFirst || isLast ? 700 : 400,
                                            color: isFirst || isLast ? "text.primary" : "text.secondary",
                                        }}
                                    >
                                        {stationName}
                                    </Typography>
                                    {platform && (
                                        <Chip
                                            label={`${t("booking.platform")} ${platform}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 18, fontSize: "0.6rem" }}
                                        />
                                    )}
                                </Stack>
                                {stationNote && (
                                    <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.25 }}>
                                        <Icon icon="mdi:information-outline" width={14} style={{ marginTop: 2, flexShrink: 0 }} />
                                        <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.3 }}>
                                            {stationNote}
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                        </Stack>
                    );
                })}
            </Box>
            </Collapse>
        </Paper>
    );
}
